/**
 * Example usage remains the same
 */
class HttpRequest {
    /**
     * Sends an HTTP request with Laravel/Sanctum CSRF support
     *
     * @param {Object} options
     * @param {string} options.method          - 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
     * @param {string} options.url             - Endpoint URL
     * @param {Object|Array|null} [options.payload=null]
     * @param {Object} [options.headers={}]
     * @param {string} [options.csrfMode='none']          - 'none' | 'meta' | 'sanctum-cookie'
     * @param {string} [options.csrfEndpoint='/sanctum/csrf-cookie']
     * @param {boolean} [options.autoRefreshCsrf=true]
     * @returns {Promise<{ ok: boolean, status: number, data: any, error?: any }>}
     * @throws {Error} only on network-level failure (not HTTP errors)
     */
    static async request({
        method = 'GET',
        url,
        payload = null,
        headers = {},
        csrfMode = 'none',
        csrfEndpoint = '/sanctum/csrf-cookie',
        autoRefreshCsrf = true,
    } = {}) {
        const httpMethod = method.toUpperCase();
        const allowed = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];
        if (!allowed.includes(httpMethod)) {
            throw new Error(`Unsupported method: ${httpMethod}`);
        }

        // ── Prepare fetch options ───────────────────────────────────────
        const options = {
            method: httpMethod,
            headers: {
                'Content-Type': 'application/json',
                ...headers,
            },
            credentials: 'same-origin',
        };

        if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(httpMethod) && payload !== null) {
            options.body = JSON.stringify(payload);
        }

        // ── CSRF handling ───────────────────────────────────────────────
        let csrfToken = null;

        if (csrfMode === 'meta') {
            const meta = document.querySelector('meta[name="csrf-token"]');
            csrfToken = meta?.getAttribute('content') ?? null;
            if (!csrfToken) {
                console.warn('CSRF meta tag not found');
            }
        }
        else if (csrfMode === 'sanctum-cookie') {
            csrfToken = HttpRequest.#getCookie('XSRF-TOKEN');

            // Auto-refresh missing CSRF token for mutating requests
            if (!csrfToken && autoRefreshCsrf && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(httpMethod)) {
                try {
                    const csrfRes = await fetch(csrfEndpoint, {
                        method: 'GET',
                        credentials: 'same-origin',
                    });
                    if (!csrfRes.ok) {
                        throw new Error(`CSRF refresh failed: ${csrfRes.status}`);
                    }
                    // Cookie should now be set
                    csrfToken = HttpRequest.#getCookie('XSRF-TOKEN');
                } catch (err) {
                    console.error('Failed to refresh Sanctum CSRF cookie:', err);
                }
            }
        }

        if (csrfToken) {
            const headerName = csrfMode === 'meta' ? 'X-CSRF-TOKEN' : 'X-XSRF-TOKEN';
            options.headers[headerName] = csrfToken;
        }

        // ── Execute request ─────────────────────────────────────────────
        let response;
        try {
            response = await fetch(url, options);
        } catch (networkErr) {
            // Network-level failure (CORS, offline, aborted, etc.)
            throw new Error(`Network error: ${networkErr.message}`);
        }

        const status = response.status;
        const ok = response.ok;

        let data = null;
        let error = null;

        const contentType = response.headers.get('content-type')?.toLowerCase() || '';

        try {
            if (contentType.includes('application/json')) {
                data = await response.json();
            } else if (contentType.includes('text/')) {
                data = await response.text();
            } else {
                // fallback — consume body anyway
                await response.text();
            }
        } catch (parseErr) {
            console.warn('Failed to parse response body:', parseErr);
            error = 'Could not parse response';
        }

        // ── Return consistent shape ─────────────────────────────────────
        return {
            ok,
            status,
            data,
            error: ok ? undefined : (data?.message || data?.error || error || `HTTP ${status} ${response.statusText}`),
        };
    }

    static #getCookie(name) {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length !== 2) return null;
        return decodeURIComponent(parts.pop().split(';').shift());
    }

    // ── Convenience methods ─────────────────────────────────────────────
    static get(url, options = {}) {
        return this.request({ method: 'GET', url, ...options });
    }

    static post(url, payload = null, options = {}) {
        return this.request({ method: 'POST', url, payload, ...options });
    }

    static put(url, payload = null, options = {}) {
        return this.request({ method: 'PUT', url, payload, ...options });
    }

    static delete(url, payload = null, options = {}) {
        return this.request({ method: 'DELETE', url, payload, ...options });
    }
}

module.exports = HttpRequest;
